import { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import {
  LogOut,
  RefreshCw,
  Phone,
  Mail,
  Wallet,
  HandCoins,
  Users2,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Inbox,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Flame,
  BookOpen,
  Download,
  Building2,
  Handshake as HandshakeIcon,
  ShieldCheck,
  XCircle,
  X,
} from "lucide-react";
import { adminApi, tokenStorage } from "@/lib/api";
import Logo from "@/components/Logo";
import BrandBanner from "@/components/BrandBanner";
import DocViewer from "@/components/DocViewer";

const TYPE_META = {
  loan: { label: "Loan", icon: HandCoins, color: "bg-amber-400/15 text-amber-200 border-amber-400/30" },
  dsa: { label: "DSA", icon: Users2, color: "bg-emerald-400/15 text-emerald-200 border-emerald-400/30" },
  advisor: { label: "Advisor", icon: Briefcase, color: "bg-blue-400/15 text-blue-200 border-blue-400/30" },
  agency: { label: "Agency", icon: Building2, color: "bg-amber-500/15 text-amber-200 border-amber-500/30" },
  associate: { label: "Associate", icon: HandshakeIcon, color: "bg-violet-400/15 text-violet-200 border-violet-400/30" },
  sub_branch: { label: "Sub Branch", icon: Building2, color: "bg-sky-400/15 text-sky-200 border-sky-400/30" },
  branch: { label: "Branch", icon: Building2, color: "bg-fuchsia-400/15 text-fuchsia-200 border-fuchsia-400/30" },
};

const ALL_TYPES = ["loan", "dsa", "advisor", "agency", "associate", "sub_branch", "branch"];
const PAGE_SIZE = 25;

export default function AdminApplications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [marking, setMarking] = useState(null); // app.id currently being updated
  const [viewer, setViewer] = useState(null); // { files, startIndex } | null
  const [decisionModal, setDecisionModal] = useState(null); // { app, mode: 'approve' | 'reject' }
  const [decisionText, setDecisionText] = useState("");
  const [decisionBusy, setDecisionBusy] = useState(false);
  const [decisionErr, setDecisionErr] = useState("");
  const [decisionTemplateKey, setDecisionTemplateKey] = useState("");
  const [rejectionTemplates, setRejectionTemplates] = useState([]);
  const [flash, setFlash] = useState(null); // { type: 'ok' | 'err', msg }
  const hasToken = Boolean(tokenStorage.get());

  const openViewer = (files, startIndex) => setViewer({ files, startIndex });
  const closeViewer = () => setViewer(null);

  const downloadDoc = async (fileId, filename) => {
    try {
      const resp = await adminApi.get(`/uploads/application-doc/${fileId}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `${fileId}.bin`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.debug("Doc download failed", e);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (typeFilter !== "all") params.type = typeFilter;
      const { data } = await adminApi.get("/applications", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, navigate]);

  useEffect(() => {
    if (hasToken) load();
  }, [load, hasToken]);

  if (!hasToken) return <Navigate to="/admin" replace />;

  const logout = async () => {
    try { await adminApi.post("/auth/logout"); } catch (_) {}
    tokenStorage.clear();
    navigate("/admin", { replace: true });
  };

  const countByType = (t) => items.filter((it) => it.type === t).length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const markPaid = async (app) => {
    if (app.payment_status === "paid") return;
    if (!window.confirm(`Mark ${app.name}'s ${app.type.toUpperCase()} application as PAID?\n\nUse this only after confirming the offline payment (UPI / bank transfer).`)) {
      return;
    }
    setMarking(app.id);
    try {
      const { data } = await adminApi.patch(`/applications/${app.id}/payment`, {
        payment_status: "paid",
      });
      setItems((prev) =>
        prev.map((it) => (it.id === app.id ? { ...it, ...data.application } : it))
      );
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not update payment status.");
    } finally {
      setMarking(null);
    }
  };

  const openDecision = (app, mode) => {
    setDecisionModal({ app, mode });
    setDecisionText("");
    setDecisionTemplateKey("");
    setDecisionErr("");
    if (mode === "reject" && rejectionTemplates.length === 0) {
      adminApi.get("/admin/rejection-templates")
        .then((r) => setRejectionTemplates(r.data.items || []))
        .catch(() => {});
    }
  };
  const closeDecision = () => {
    if (decisionBusy) return;
    setDecisionModal(null);
    setDecisionText("");
    setDecisionTemplateKey("");
    setDecisionErr("");
  };

  const applyTemplate = (key) => {
    setDecisionTemplateKey(key);
    const tpl = rejectionTemplates.find((t) => t.key === key);
    if (tpl) setDecisionText(tpl.body || "");
  };

  const submitDecision = async () => {
    if (!decisionModal) return;
    const { app, mode } = decisionModal;
    setDecisionErr("");
    if (mode === "reject" && decisionText.trim().length < 6) {
      setDecisionErr("Please share a short reason (at least 6 characters) so the applicant knows what to improve.");
      return;
    }
    setDecisionBusy(true);
    try {
      const body = mode === "approve"
        ? { note: decisionText.trim() || null }
        : { reason: decisionText.trim(), template_key: decisionTemplateKey || null };
      const { data } = await adminApi.post(`/admin/applications/${app.id}/${mode}`, body);
      setItems((prev) => prev.map((it) => it.id === app.id ? { ...it, status: data.status, reference_id: data.reference_id || it.reference_id } : it));
      setFlash({
        type: "ok",
        msg: mode === "approve"
          ? `Approved · Onboarding pack (Welcome + Appointment + Agreement PDFs) is being emailed to ${app.name}. Reference ${data.reference_id}.`
          : `Rejected · A polite rejection email has been queued for ${app.name}.`,
      });
      setDecisionModal(null);
      setDecisionText("");
    } catch (err) {
      setDecisionErr(err?.response?.data?.detail || "Could not complete the action. Please try again.");
    } finally {
      setDecisionBusy(false);
    }
  };

  return (
    <div data-testid="admin-applications-page" className="min-h-screen bg-[#04102B] text-white">
      <header className="sticky top-0 z-30 bg-[#04102B]/90 backdrop-blur-md border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[140px] flex items-center justify-between gap-4">
          <button
            data-testid="apps-back-home"
            onClick={() => navigate("/")}
            className="flex items-center gap-4 text-left"
          >
            <div className="leading-tight">
              <div className="font-display text-sm md:text-lg uppercase tracking-tight font-semibold whitespace-nowrap">
                SAHYOG FINANCE
                <br />
                <span className="text-[#D4AF37]">&amp; NOBLE INFINITY</span>
              </div>
              <div className="font-display text-[#D4AF37] text-[10px] md:text-xs tracking-[0.18em] uppercase mt-1">
                Asset Management Pvt. Ltd. · Admin Console
              </div>
            </div>
          </button>
          <div className="flex items-center gap-3">
            <button
              data-testid="apps-refresh"
              onClick={load}
              disabled={loading}
              className="text-white/80 hover:text-[#F0D788] inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/10 text-base font-semibold"
            >
              <RefreshCw size={20} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <a href="/admin/change-password" data-testid="admin-change-pw-link" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 text-[#F0D788] text-base font-semibold">Change password</a>
            <button
              data-testid="apps-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 text-sm border border-white/10"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        {/* Admin nav tabs */}
        <div className="flex items-center gap-3">
          <Link
            data-testid="nav-tab-leads"
            to="/admin/leads"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
          >
            <Inbox size={14} /> Leads
          </Link>
          <span
            data-testid="nav-tab-applications"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37] text-[#0A1F44] border border-[#D4AF37] text-sm font-semibold"
          >
            <HandCoins size={14} /> Applications ({total})
          </span>
          <Link
            data-testid="nav-tab-grievances"
            to="/admin/grievances"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
          >
            <AlertCircle size={14} /> Grievances
          </Link>
          <Link
            data-testid="nav-tab-offers"
            to="/admin/offers"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
          >
            <Flame size={14} /> Hot Offers
          </Link>
          <Link
            data-testid="nav-tab-onboarding-board"
            to="/admin/onboarding"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
          >
            <ShieldCheck size={14} /> Onboarding Board
          </Link>
          <Link
            data-testid="nav-tab-blogs"
            to="/admin/blogs"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
          >
            <BookOpen size={14} /> Blogs
          </Link>
        </div>

        {/* Type filter chips */}
        <div className="mt-6 flex flex-wrap gap-2">
          <button
            data-testid="filter-type-all"
            onClick={() => { setTypeFilter("all"); setPage(0); }}
            className={`px-3 py-1.5 rounded-full text-xs border transition ${
              typeFilter === "all"
                ? "bg-[#D4AF37] text-[#0A1F44] border-[#D4AF37]"
                : "bg-white/[0.04] text-white/75 border-white/10 hover:border-[#D4AF37]/40"
            }`}
          >
            All ({total})
          </button>
          {ALL_TYPES.map((t) => (
            <button
              key={t}
              data-testid={`filter-type-${t}`}
              onClick={() => { setTypeFilter(t); setPage(0); }}
              className={`px-3 py-1.5 rounded-full text-xs border transition capitalize ${
                typeFilter === t
                  ? "bg-[#D4AF37] text-[#0A1F44] border-[#D4AF37]"
                  : "bg-white/[0.04] text-white/75 border-white/10 hover:border-[#D4AF37]/40"
              }`}
            >
              {TYPE_META[t].label} ({countByType(t)})
            </button>
          ))}
        </div>

        {/* Applications table */}
        <section className="mt-6 rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="apps-table" className="w-full text-sm">
              <thead className="bg-white/[0.04] text-white/55 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left p-4">Type</th>
                  <th className="text-left p-4">Applicant</th>
                  <th className="text-left p-4">Contact</th>
                  <th className="text-left p-4">Payment</th>
                  <th className="text-left p-4">Created</th>
                  <th className="text-left p-4">Detail</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="p-10 text-center text-white/70">Loading…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={6} className="p-10 text-center text-white/70">No applications yet.</td></tr>
                )}
                {!loading && items.map((app) => {
                  const M = TYPE_META[app.type] || TYPE_META.loan;
                  const Icon = M.icon;
                  const isOpen = expanded === app.id;
                  return (
                    <Fragment key={app.id}>
                      <tr data-testid={`app-row-${app.id}`} className="border-t border-white/5 hover:bg-white/[0.03]">
                        <td className="p-4 align-top">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] uppercase tracking-wider ${M.color}`}>
                            <Icon size={11} /> {M.label}
                          </span>
                        </td>
                        <td className="p-4 align-top">
                          <div className="font-display text-base">{app.name}</div>
                          <div className="text-[11px] text-white/40 mt-1">ID · {app.id.slice(0, 8)}</div>
                          {app.status === "approved" && (
                            <span data-testid={`app-status-approved-${app.id}`} className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-emerald-500/15 border border-emerald-400/40 text-emerald-200">
                              <ShieldCheck size={10} /> Approved
                            </span>
                          )}
                          {app.status === "rejected" && (
                            <span data-testid={`app-status-rejected-${app.id}`} className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-rose-500/15 border border-rose-400/40 text-rose-200">
                              <XCircle size={10} /> Rejected
                            </span>
                          )}
                        </td>
                        <td className="p-4 align-top">
                          <a href={`tel:${app.phone}`} className="flex items-center gap-1.5 text-white/85 hover:text-[#F0D788]">
                            <Phone size={11} className="text-[#D4AF37]" /> {app.phone}
                          </a>
                          {app.email && (
                            <a href={`mailto:${app.email}`} className="flex items-center gap-1.5 text-white/60 text-xs mt-1 hover:text-[#F0D788]">
                              <Mail size={10} className="text-[#D4AF37]" /> {app.email}
                            </a>
                          )}
                        </td>
                        <td className="p-4 align-top">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] border uppercase tracking-wider ${
                            app.payment_status === "paid" ? "bg-emerald-400/15 text-emerald-200 border-emerald-400/30"
                            : app.payment_status === "pending" ? "bg-yellow-300/15 text-yellow-200 border-yellow-300/30"
                            : "bg-zinc-400/10 text-zinc-300 border-zinc-400/25"
                          }`}>
                            <Wallet size={11} /> {app.payment_status}
                          </span>
                          {app.payment_status === "pending" && (
                            <button
                              data-testid={`app-mark-paid-${app.id}`}
                              onClick={() => markPaid(app)}
                              disabled={marking === app.id}
                              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/40 text-emerald-200 text-[11px] uppercase tracking-wider transition disabled:opacity-50"
                            >
                              {marking === app.id ? (
                                <><Loader2 size={11} className="animate-spin" /> Updating…</>
                              ) : (
                                <><CheckCircle2 size={11} /> Mark Paid</>
                              )}
                            </button>
                          )}
                          {app.payment_marked_by && app.payment_status === "paid" && (
                            <div className="mt-1.5 text-[10px] text-white/40 lowercase">
                              via {app.payment_marked_by}
                            </div>
                          )}
                        </td>
                        <td className="p-4 align-top text-white/65 whitespace-nowrap">
                          {new Date(app.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </td>
                        <td className="p-4 align-top">
                          <button
                            data-testid={`app-toggle-${app.id}`}
                            onClick={() => setExpanded(isOpen ? null : app.id)}
                            className="inline-flex items-center gap-1 text-[#F0D788] hover:underline text-xs"
                          >
                            {isOpen ? <><ChevronUp size={12} /> Hide</> : <><ChevronDown size={12} /> View</>}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-white/5 bg-white/[0.02]">
                          <td colSpan={6} className="p-5">
                            {Array.isArray(app.documents) && app.documents.length > 0 && (
                              <div className="mb-5">
                                <div className="text-[11px] uppercase tracking-wider text-white/55 mb-2 flex items-center justify-between gap-2 flex-wrap">
                                  <span>Uploaded Documents · {app.documents.length}</span>
                                  <div className="flex items-center gap-2">
                                    <a
                                      data-testid={`app-doc-pack-${app.id}`}
                                      href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/applications/${app.id}/document-pack.pdf`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37] text-[#0A1F44] hover:brightness-110 text-[10px] uppercase tracking-[0.14em] font-semibold shadow-lg shadow-[#D4AF37]/20"
                                    >
                                      <Download size={11} /> Download Pack
                                    </a>
                                    <button
                                      data-testid={`app-view-all-docs-${app.id}`}
                                      onClick={() => openViewer(app.documents, 0)}
                                      className="text-[10px] uppercase tracking-[0.18em] text-[#F0D788] hover:underline"
                                    >
                                      View all →
                                    </button>
                                  </div>
                                </div>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {app.documents.map((d, di) => (
                                    <div
                                      key={d.id}
                                      data-testid={`app-doc-${d.id}`}
                                      className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-[#D4AF37]/25 bg-[#0A1F44]"
                                    >
                                      <button
                                        onClick={() => openViewer(app.documents, di)}
                                        data-testid={`app-doc-preview-${d.id}`}
                                        className="min-w-0 text-left flex-1 hover:text-[#F0D788]"
                                      >
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37]">{d.kind}</div>
                                        <div className="text-xs text-white/80 truncate">{d.filename}</div>
                                      </button>
                                      <button
                                        onClick={() => downloadDoc(d.id, d.filename)}
                                        data-testid={`app-doc-download-${d.id}`}
                                        aria-label="Download"
                                        title="Download"
                                        className="w-7 h-7 grid place-items-center rounded-md border border-[#D4AF37]/30 text-[#F0D788] hover:bg-[#D4AF37]/15"
                                      >
                                        <Download size={12} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="text-[11px] uppercase tracking-wider text-white/55 mb-2">
                              Full Application Payload
                            </div>
                            <pre className="text-xs bg-black/30 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap text-white/85">
                              {JSON.stringify(app.payload, null, 2)}
                            </pre>

                            {/* Approve / Reject decision bar (partner apps only) */}
                            {ALL_TYPES.filter((t) => t !== "loan").includes(app.type) && (
                              <div className="mt-6 rounded-xl border border-[#D4AF37]/25 bg-[#0A1F44]/60 p-5">
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                  <div>
                                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-1">
                                      Onboarding Decision
                                    </div>
                                    <div className="text-white/90 text-sm">
                                      {app.status === "approved" && (
                                        <span className="inline-flex items-center gap-2 text-emerald-300">
                                          <ShieldCheck size={14} /> Approved · Ref {app.reference_id || "—"}
                                        </span>
                                      )}
                                      {app.status === "rejected" && (
                                        <span className="inline-flex items-center gap-2 text-rose-300">
                                          <XCircle size={14} /> Rejected · {app.rejection_reason || "reason recorded"}
                                        </span>
                                      )}
                                      {app.status !== "approved" && app.status !== "rejected" && (
                                        <>Review documents above, then Approve to auto-create the login & email the onboarding pack, or Reject with a reason.</>
                                      )}
                                    </div>
                                  </div>
                                  {app.status !== "approved" && app.status !== "rejected" && (
                                    <div className="flex gap-2">
                                      <button
                                        data-testid={`app-reject-${app.id}`}
                                        onClick={() => openDecision(app, "reject")}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-rose-400/40 text-rose-200 hover:bg-rose-500/15 text-xs uppercase tracking-wider transition"
                                      >
                                        <XCircle size={13} /> Reject
                                      </button>
                                      <button
                                        data-testid={`app-approve-${app.id}`}
                                        onClick={() => openDecision(app, "approve")}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37] text-[#0A1F44] hover:brightness-110 text-xs uppercase tracking-wider font-semibold shadow-lg shadow-[#D4AF37]/20 transition"
                                      >
                                        <ShieldCheck size={13} /> Approve &amp; Send Pack
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Pagination */}
        <section className="mt-6 flex items-center justify-between text-sm">
          <div className="text-white/55">
            Page <span className="text-white">{page + 1}</span> of {pageCount} · {total} total
          </div>
          <div className="flex gap-2">
            <button
              data-testid="apps-prev"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/10 hover:border-[#D4AF37]/40 text-xs disabled:opacity-40"
            >
              <ChevronLeft size={13} /> Previous
            </button>
            <button
              data-testid="apps-next"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage((p) => p + 1)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/10 hover:border-[#D4AF37]/40 text-xs disabled:opacity-40"
            >
              Next <ChevronRight size={13} />
            </button>
          </div>
        </section>
      </main>

      {viewer && (
        <DocViewer
          files={viewer.files}
          startIndex={viewer.startIndex}
          onClose={closeViewer}
        />
      )}

      {/* Flash banner (approve/reject outcome) */}
      {flash && (
        <div
          data-testid="apps-flash"
          className={`fixed bottom-6 right-6 z-40 max-w-md rounded-2xl border p-4 shadow-2xl relative ${
            flash.type === "ok"
              ? "bg-emerald-500/10 border-emerald-400/40 text-emerald-100"
              : "bg-rose-500/10 border-rose-400/40 text-rose-100"
          }`}
        >
          <div className="flex items-start gap-3">
            {flash.type === "ok" ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <AlertCircle size={18} className="mt-0.5 shrink-0" />}
            <div className="text-sm leading-relaxed pr-6">{flash.msg}</div>
            <button
              data-testid="apps-flash-dismiss"
              onClick={() => setFlash(null)}
              className="absolute top-2 right-2 opacity-70 hover:opacity-100"
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Approve / Reject modal */}
      {decisionModal && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeDecision}
        >
          <div
            data-testid={`decision-modal-${decisionModal.mode}`}
            className="w-full max-w-xl bg-[#04102B] border border-[#D4AF37]/30 rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]">
                  {decisionModal.mode === "approve" ? "Approve Application" : "Reject Application"}
                </div>
                <div className="mt-1 font-display text-lg text-white">
                  {decisionModal.app.name} · <span className="text-white/60">{decisionModal.app.type.toUpperCase()}</span>
                </div>
              </div>
              <button
                data-testid="decision-close"
                onClick={closeDecision}
                className="text-white/50 hover:text-white"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {decisionModal.mode === "approve" ? (
                <>
                  <p className="text-sm text-white/80 leading-relaxed">
                    On approval we will automatically:
                  </p>
                  <ul className="text-xs text-white/70 space-y-1.5 pl-4 list-disc">
                    <li>Create a Partner Portal login for <b className="text-white/90">{decisionModal.app.email || decisionModal.app.phone}</b></li>
                    <li>Generate personalised <b className="text-white/90">Welcome Letter, Appointment Letter, and Partner Agreement</b> PDFs</li>
                    <li>Email all three attachments with a <b className="text-white/90">24-hour secure password-setup link</b></li>
                    <li>Kick off the Day 1 / Day 3 / Day 7 / Day 11 onboarding email drip</li>
                  </ul>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-white/55" htmlFor="approve-note">
                      Internal note (optional)
                    </label>
                    <textarea
                      id="approve-note"
                      data-testid="approve-note"
                      rows={3}
                      value={decisionText}
                      onChange={(e) => setDecisionText(e.target.value)}
                      placeholder="Anything the audit log should remember about this approval…"
                      className="mt-1 w-full rounded-lg bg-[#0A1F44] border border-white/10 focus:border-[#D4AF37] outline-none text-sm text-white/90 p-3"
                    />
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-white/80 leading-relaxed">
                    A polite rejection email will be sent to <b className="text-white/95">{decisionModal.app.email || "the applicant"}</b> with the reason below. This action cannot be undone from this screen.
                  </p>

                  {rejectionTemplates.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-white/55 mb-2">
                        Quick reason (one click)
                      </div>
                      <div className="flex flex-wrap gap-1.5" data-testid="reject-template-chips">
                        {rejectionTemplates.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            data-testid={`reject-template-${t.key}`}
                            onClick={() => applyTemplate(t.key)}
                            className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider border transition ${
                              decisionTemplateKey === t.key
                                ? "bg-rose-500/25 border-rose-400/60 text-rose-100 font-semibold"
                                : "bg-white/[0.05] border-white/15 text-white/70 hover:border-rose-400/40"
                            }`}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                      <div className="mt-1.5 text-[10px] text-white/40">
                        Selecting a template fills the reason below — you can still edit it before sending.
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-white/55" htmlFor="reject-reason">
                      Reason for rejection (visible to applicant) <span className="text-rose-300">*</span>
                    </label>
                    <textarea
                      id="reject-reason"
                      data-testid="reject-reason"
                      rows={5}
                      value={decisionText}
                      onChange={(e) => setDecisionText(e.target.value)}
                      placeholder="E.g. Aadhaar mismatch with declared address. Please reapply after re-uploading a matching KYC set."
                      className="mt-1 w-full rounded-lg bg-[#0A1F44] border border-rose-400/30 focus:border-rose-300 outline-none text-sm text-white/90 p-3"
                    />
                  </div>
                </>
              )}
              {decisionErr && (
                <div data-testid="decision-err" className="text-xs text-rose-300 flex items-start gap-2">
                  <AlertCircle size={13} className="mt-0.5" /> {decisionErr}
                </div>
              )}
            </div>
            <div className="px-6 py-4 bg-white/[0.03] border-t border-white/10 flex items-center justify-end gap-2">
              <button
                data-testid="decision-cancel"
                onClick={closeDecision}
                disabled={decisionBusy}
                className="px-4 py-2 rounded-full text-xs uppercase tracking-wider text-white/70 hover:text-white border border-white/10"
              >
                Cancel
              </button>
              <button
                data-testid="decision-confirm"
                onClick={submitDecision}
                disabled={decisionBusy}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs uppercase tracking-wider font-semibold transition ${
                  decisionModal.mode === "approve"
                    ? "bg-[#D4AF37] text-[#0A1F44] hover:brightness-110"
                    : "bg-rose-500 text-white hover:brightness-110"
                } disabled:opacity-60`}
              >
                {decisionBusy ? (
                  <><Loader2 size={13} className="animate-spin" /> Working…</>
                ) : decisionModal.mode === "approve" ? (
                  <><ShieldCheck size={13} /> Approve &amp; Send Pack</>
                ) : (
                  <><XCircle size={13} /> Send Rejection</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
